import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { HardDrive, Trash2, FolderTree, Plus } from "lucide-react";
import { api } from "../lib/api";
import { formatBytes, formatDate, formatNumber } from "../lib/format";

export default function DiskListPage(): JSX.Element {
  const qc = useQueryClient();
  const { data: disks = [], isLoading } = useQuery({
    queryKey: ["disks"],
    queryFn: () => api.listDisks(),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteDisk(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["disks"] }),
  });
  const totalFiles = disks.reduce((acc, d) => acc + d.fileCount, 0);
  const totalSize = disks.reduce((acc, d) => acc + (d.usedSize ?? 0), 0);

  return (
    <div className="space-y-8">
      <section className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-2">Bibliothèque</div>
          <h1 className="title-dual text-h1">
            {disks.length} {disks.length <= 1 ? "disque indexé" : "disques indexés"}{" "}
            <span className="dim">
              · {formatNumber(totalFiles)} fichiers · {formatBytes(totalSize)}
            </span>
          </h1>
        </div>
        <Link to="/scan" className="pill-solid">
          <Plus className="h-4 w-4" />
          Indexer un disque
        </Link>
      </section>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card-koloc h-48 shimmer rounded-md" />
          ))}
        </div>
      ) : disks.length === 0 ? (
        <div className="card-koloc text-center py-16">
          <HardDrive className="mx-auto mb-4 h-10 w-10 text-foreground/30" />
          <h3 className="text-h3 font-semibold">Aucun disque encore indexé</h3>
          <p className="mt-2 text-foreground/50 text-body max-w-md mx-auto">
            Branche un premier disque externe et lance un scan pour bâtir la bibliothèque.
          </p>
          <Link to="/scan" className="pill-solid mt-6">
            <Plus className="h-4 w-4" />
            Premier scan
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {disks.map((d) => (
            <div key={d.id} className="card-koloc card-lift flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 grid h-10 w-10 place-items-center rounded-md bg-foreground/8">
                    <HardDrive className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-body font-semibold">{d.label}</div>
                    {d.volumeName && (
                      <div className="truncate text-caption text-foreground/50">{d.volumeName}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3 text-caption">
                <div>
                  <div className="label-caps">Fichiers</div>
                  <div className="mt-1 num">{formatNumber(d.fileCount)}</div>
                </div>
                <div>
                  <div className="label-caps">Taille</div>
                  <div className="mt-1 num">{formatBytes(d.usedSize)}</div>
                </div>
                <div>
                  <div className="label-caps">Scanné</div>
                  <div className="mt-1 num">{formatDate(d.scannedAt)}</div>
                </div>
              </div>

              {d.notes && (
                <p className="mt-4 text-caption text-foreground/60 line-clamp-2">{d.notes}</p>
              )}

              <div className="mt-auto pt-5 flex items-center gap-2">
                <Link
                  to={`/disques/${d.id}`}
                  className="pill-outline pill-sm flex-1 justify-center"
                >
                  <FolderTree className="h-3.5 w-3.5" />
                  Explorer
                </Link>
                <button
                  onClick={() => {
                    if (confirm(`Supprimer "${d.label}" de l'index ? Le disque physique reste intact.`)) {
                      deleteMut.mutate(d.id);
                    }
                  }}
                  disabled={deleteMut.isPending}
                  className="pill-outline pill-sm text-destructive border-destructive/20 hover:bg-destructive/5"
                  aria-label="Supprimer"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
